import React, { memo } from 'react';
import cn from 'classnames';

import { Checkbox, DynamicIconWrapper, Icons, Tooltip } from '@components/common';
import FlatTreeChevron from '@components/common/FlatTree/FlatTreeNodeWrapper/FlatTreeChevron';

import { IFlatMoTreeNode } from '@interfaces/components/tree';

import { isMavenirNodeObject } from '../../utils';
import TreeSelect from './TreeSelect';

import styles from './FlatTreeMavenirNode.module.scss';

interface IFlatTreeMavenirNodeProps {
	node: IFlatMoTreeNode;
	skipNodeHandler: () => void;
	toggleCollapse: () => void;
	onSelect: (item: IFlatMoTreeNode, selected: string, isChecked: boolean) => void;
	isCollapsed: boolean;
	isReadOnly?: boolean;
	getCheckboxLabel?: (value: boolean) => string;
	isSingleSelectMode?: boolean;
	brackets?: {
		openedBracket?: string;
		closedBracket?: string;
	};
	index: string;
	isWithFolderIcon?: boolean;
	isDisabled?: boolean;
}

const FlatTreeMavenirNode = ({
	node,
	skipNodeHandler,
	toggleCollapse,
	getCheckboxLabel,
	isCollapsed,
	isSingleSelectMode,
	onSelect,
	isReadOnly,
	isWithFolderIcon = false,
	isDisabled,
}: IFlatTreeMavenirNodeProps) => {
	const nodeContentClasses = cn(styles.nodeContent, {
		[styles.readOnlyPadding]: isReadOnly && !node.nodeType,
	});

	const isNodeObject = isMavenirNodeObject(node);

	const isIndefinite = !node.isChecked
		&& isNodeObject
		&& (node.countOfChecked || 0) > 0
		&& node.countOfChecked !== node.countOfChildren;

	return (
		<div className={styles.container}>
			<div
				className={styles.node}
				data-testid={`node_${node.path}`}
			>
				{node.nodeType &&
					<FlatTreeChevron
						isCollapsed={isCollapsed}
						testName={node.path}
						toggleCollapse={toggleCollapse}
					/>
				}
				<Tooltip
					content={getCheckboxLabel ? getCheckboxLabel(node.isChecked) : undefined}
					isDisabled={!getCheckboxLabel}
					placement='top'
				>
					<span className={styles.checkboxWrapper}>
						<Checkbox
							checked={node.isChecked}
							disabled={isDisabled}
							isIndefinite={isIndefinite}
							name={node.path}
							onChange={skipNodeHandler}
						/>
					</span>
				</Tooltip>
				{node.nodeType && isWithFolderIcon &&
					Icons.folderIcon()
				}
				<div className={nodeContentClasses}>
					<span
						className={node.nodeType ? styles.nodeKey : undefined}
						data-testid='node_key'
					>
						{node.key}
					</span>
					{!!node.checkboxList?.length &&
						<TreeSelect
							data={node.checkboxList}
							isSingleSelectMode={isSingleSelectMode}
							node={node}
							selectTree={onSelect}
						/>
					}
				</div>
				{node.description && isNodeObject &&
					<DynamicIconWrapper
						icon={Icons.info}
						tooltipLabel={node.description}
					/>
				}
			</div>
			{node.tags &&
				<div className={styles.tagsContainer}>
					{node.tags.map(tag => (
						<span
							key={`tag_${node.path}_${tag}`}
							className={styles.tag}
						>{tag}</span>
					))}
				</div>
			}
		</div>
	);
};

export default memo(FlatTreeMavenirNode);
